import { Badge, Box, Button, Container, Flex } from "@chakra-ui/react";
import React, { MutableRefObject, useRef, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";

import { updateAnswer, updateTimeLeft } from "../../shared/Api";
import Markdown from "../../shared/components/Markdown";
import RenderResource from "../../shared/components/RenderResource";
import RenderText from "../../shared/components/RenderText";
import TimerComponent from "../../shared/components/TimerComponent";
import {useFont} from "../../shared/hooks/FontProvider";
import {useTheme} from "../../shared/hooks/ThemeProvider";
import { CandidateStatus , customButtonProps, TestType } from "../../shared/model/Constants";
import { StatusDto, TestDto } from "../../shared/model/Dto";
import { publicKeys } from "../../shared/QueryKeys";
import { getUniqueOrigin, isInAvataiSite, validResourceExtension } from "../../shared/Utils";
import Options from "./Options";

type QuestionProps = {
  test: TestDto;
  status: StatusDto;
  isLoading: boolean;
  onNextQuestion: (optionIds: string[], timeLeft: number) => void;
  onReRender?: () => void;
  timesPlayed?: MutableRefObject<number>;
  timeLeft?: MutableRefObject<number>;
};

// Rerendering this component does not rerender the entire component.
// audio elements on other components don't stop when this is rerendered
const OptionsSection = (props: QuestionProps) => {
  const theme = useTheme().theme;
  const font = useFont().font;
  const isAvatai = isInAvataiSite();
    const [optionIds, setOptionIds] = useState<string[]>([]);
    return (
        <><Options
            multipleChoice={props.test.nextQuestion.multipleChoice}
            options={props.test.nextQuestion.options}
            isLoading={props.isLoading}
            optionIds={optionIds}
            onChange={setOptionIds}

        />
        <Box textAlign="right">
            <Button
                onClick={() => {
                    if (props.timesPlayed && props.timeLeft) {
                        props.test.nextQuestion.timesPlayed = props.timesPlayed.current;
                        props.onNextQuestion(optionIds, props.timeLeft.current);
                        // props.onNextQuestion(optionIds, props.timesPlayed);
                        setOptionIds([]);
                        props.timesPlayed.current = 0;
                        // props.timesPlayed.current = props.test.nextQuestion.timesPlayed;
                    }
                    if (props.onReRender) props.onReRender();
                }}
                isLoading={props.isLoading}
                disabled={!optionIds.length}
                {...customButtonProps(isAvatai)}
                fontWeight={font.weight}
                fontSize={font.buttonText}
                _dark={{ color: "black" }}
            >
                Continue
            </Button>
        </Box></>
    )
}

const Question = (props: QuestionProps) => {
  const timesPlayedRef = useRef<number>(props.test.nextQuestion.timesPlayed);
  const timeLeft = useRef<number>(props.test.timeLeft);
  const [reRender, setReRender] = useState<boolean>(false);

  const queryClient = useQueryClient();
  const params = useParams<{
    publicId: string;
    candidateToken: string;
  }>();
  const publicId = params.publicId || "";
  const candidateToken = params.candidateToken || "";

  // For questions that have text with bold and non-bold sections
  let parsedText=
    props.test.nextQuestion.text.startsWith('**') ?
      `${props.test.nextQuestion.text.slice(2)}` :
      `**${props.test.nextQuestion.text}`;

  parsedText = parsedText.endsWith('**') ?
    parsedText.slice(0,parsedText.length-2) :
    parsedText+'**';

  /*const postAudioPlayedMutation = useMutation(
    ({ questionId, timesPlayed, timeLeft }: any) =>
      updateAnswer(publicId, candidateToken, questionId, timesPlayed, timeLeft),
    {
      onSuccess: () =>
        queryClient.invalidateQueries(publicKeys.testsByPublicId(publicId)),
    }
  );*/

  // mutation interrupts audio reproduction
  function postAudioPlayed (questionId: string, timesPlayed: number, timeLeft: number) {
    timesPlayedRef.current = timesPlayed;
    return updateAnswer(publicId, candidateToken, questionId, timesPlayed, timeLeft);
  }

  const postUpdateTimeLeftMutation = useMutation(
    ({ timeLeft }: any) =>
      updateTimeLeft(publicId, candidateToken, timeLeft),
    {
      onSuccess: () =>
        queryClient.invalidateQueries(publicKeys.testsByPublicId(publicId)),
    }
  );

  //if (props.test.timeLeft !== 0)
  //  window.addEventListener("beforeunload", (ev) => {
  //    return postUpdateTimeLeftMutation.mutate(timeLeft.current);
  //  });

  const handleReRender = () => {
    setReRender(!reRender);
  };

    return (
      <Container padding="5" maxW="max" aria-hidden="true">
        { props.test.timeLimit !== 0 &&
        <TimerComponent timeLeft={timeLeft}
                        onTimerFinished={(timeLeft: number) => {
                          // only ends test if it is single adaptive, not multistage
                          //if (props.test.testType === TestType.ADAPTIVE &&
                          //  (props.test.conditions && props.test.conditions.includes("ADAPTIVE"))
                          //)
                            postUpdateTimeLeftMutation.mutate({ timeLeft });
                          }
                        }
                        />
        }
        <Flex justify="center" align="space-between">
            <Box textAlign="right" paddingRight={10}>
                <Badge>#{props.test.status.answered + 1}</Badge>
            </Box>
            <Box boxSize="xl">
                { props.test.nextQuestion.introText != null &&
                  <RenderText id={props.test.nextQuestion.introText}
                              content={`**${props.test.nextQuestion.introText}**`}
                               />
                }
                { validResourceExtension(props.test.nextQuestion.resource)
                    ? <RenderResource
                        resource={props.test.nextQuestion.resource}
                        timesPlayed={timesPlayedRef}
                        subdirectory={"Tests/"+ (getUniqueOrigin(props.test.nextQuestion)===''  ?
                            props.test.publicId : getUniqueOrigin(props.test.nextQuestion))}
                        conditions={props.test.conditions}
                        onAudioPlayed={(timesPlayed: number) =>
                          postAudioPlayed(
                              props.test.nextQuestion.id,
                              timesPlayed,
                              timeLeft.current
                            )
                        }
                    />
                    : <Markdown content={props.test.nextQuestion.resource} />
                }
            </Box>
            <Box paddingTop={10} paddingLeft={5}  boxSize="lg">
                { props.test.nextQuestion.text != null &&
                  <Box paddingBottom={5}>
                    <RenderText id={props.test.nextQuestion.text} debug={true}
                                content={parsedText} />
                  </Box>
                }

              <OptionsSection  isLoading={props.isLoading}
                               onNextQuestion={props.onNextQuestion}
                               status={props.status}
                               test={props.test}
                               onReRender={handleReRender}
                               timesPlayed={timesPlayedRef}
                               timeLeft={timeLeft}
              />
            </Box>
        </Flex>
      </Container>
    );
};

export default Question;
